import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { getPendingSuggestionsCount } from '../api';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import { useToast } from '../context/ToastContext';
import { Phone, Calendar, Clock, Flame, ArrowUpRight, ArrowDownRight, MessageCircle, Sparkles, Download, Users, FileText, Sun, PhoneCall, PartyPopper, Rocket, CheckCircle2, Circle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import HotLeadsLeaderboard from '../components/HotLeadsLeaderboard';
import UsageMeter from '../components/UsageMeter';
import PaymentFailureBanner from '../components/PaymentFailureBanner';
import WelcomeModal from '../components/WelcomeModal';
import OnboardingChecklist from '../components/OnboardingChecklist';
import { Skeleton, SkeletonStatGrid, SkeletonChart } from '../components/Skeleton';

const QuickAction = React.memo(function QuickAction({ icon: Icon, label, subtext, color, onClick, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="text-left bg-navy-800 border border-navy-600 rounded-lg p-4 hover:border-accent-blue/40 hover:bg-navy-700/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed group"
      data-testid={`quick-action-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className={`flex items-center gap-2 mb-1 ${color}`}>
        <Icon size={16} className="group-hover:scale-110 transition-transform" />
        <span className="text-sm font-medium text-white">{label}</span>
      </div>
      <div className="text-xs text-slate-500">{subtext}</div>
    </button>
  );
});

const StatCard = React.memo(function StatCard({ label, value, subtext, icon: Icon, iconColor, trend, trendUp, prevCount }) {
  // If previous period had < 5 items and trend is huge, show absolute instead
  const showAbsolute = prevCount !== undefined && prevCount < 5 && Math.abs(trend || 0) > 200;
  return (
    <div className="bg-navy-800 border border-navy-600 rounded-lg p-6 transition-colors hover:border-navy-700" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-start justify-between mb-4">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconColor}`}>
          <Icon size={18} />
        </div>
        {trend !== undefined && trend !== 0 && (
          <div className={`flex items-center gap-1 text-xs font-medium ${trendUp ? 'text-accent-green' : 'text-accent-red'}`}>
            {trendUp ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
            {showAbsolute ? `+${Math.abs(value - (prevCount || 0))} flere` : `${Math.abs(trend)}%`}
          </div>
        )}
      </div>
      <div className="text-2xl font-heading font-bold text-white">{value}</div>
      <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider">{label}</div>
      {subtext && <div className="text-xs text-slate-400 mt-2">{subtext}</div>}
    </div>
  );
});

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const [analytics, setAnalytics] = useState(null);
  const [recentCalls, setRecentCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingSuggestions, setPendingSuggestions] = useState(0);
  const [showWelcomeBanner, setShowWelcomeBanner] = useState(false);
  const [company, setCompany] = useState(null);
  const [showFirstCallCelebration, setShowFirstCallCelebration] = useState(false);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [onboardingError, setOnboardingError] = useState(false);
  const [onboardingRetryNonce, setOnboardingRetryNonce] = useState(0);

  useEffect(() => {
    async function load() {
      try {
        const [analyticsRes, callsRes] = await Promise.all([
          api.get('/api/analytics?days=30'),
          api.get('/api/calls?limit=5&sort=created_at&order=desc')
        ]);
        setAnalytics(analyticsRes.data);
        const calls = callsRes.data.calls || [];
        setRecentCalls(calls);
        // Show welcome banner for new clients with < 5 total calls
        const dismissKey = 'welcome-banner-dismissed';
        if (calls.length < 5 && !localStorage.getItem(dismissKey)) {
          setShowWelcomeBanner(true);
        }
        // First-call celebration: total === 1 and not dismissed
        const totalCalls = analyticsRes.data?.total_calls || 0;
        const firstCallKey = 'first-call-celebrated';
        if (totalCalls === 1 && !localStorage.getItem(firstCallKey)) {
          setShowFirstCallCelebration(true);
        }
        // Fetch company info for the Test widget (phone number)
        try {
          const companyRes = await api.get('/api/company').catch(() => api.get('/api/settings/company'));
          setCompany(companyRes.data);
        } catch (_) { /* no-op */ }
        // Fetch onboarding status for the go-live checklist card.
        // On 404 / network error, surface an error state instead of silently
        // hiding the card so the user knows something went wrong and can retry.
        try {
          const onbRes = await api.get('/api/onboarding-status');
          setOnboardingStatus(onbRes.data);
          setOnboardingError(false);
        } catch (e) {
          setOnboardingStatus(null);
          setOnboardingError(true);
        }
      } catch (err) {
        console.error(err);
        toast.error('Kunne ikke laste dashbord-data');
      } finally {
        setLoading(false);
      }
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboardingRetryNonce]);

  useEffect(() => {
    if (!user?.company_id) return;
    getPendingSuggestionsCount(user.company_id)
      .then(res => setPendingSuggestions(res.data.pending || 0))
      .catch(() => {});
  }, [user?.company_id]);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <div>
          <Skeleton height={28} width={220} className="mb-2" />
          <Skeleton height={12} width={320} />
        </div>
        <SkeletonStatGrid count={4} />
        <Skeleton height={64} className="w-full" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2"><SkeletonChart height={240} /></div>
          <div className="bg-navy-800 border border-navy-600 rounded-lg p-6 space-y-3">
            <Skeleton height={14} width={140} className="mb-3" />
            {[1,2,3,4,5].map(i => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton height={32} width={32} rounded="rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton height={10} className="w-3/4" />
                  <Skeleton height={8} className="w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const a = analytics || {};
  const avgSec = Math.round(a.avg_duration || 0);
  const avgMin = avgSec > 0 ? `${Math.floor(avgSec / 60)}m ${avgSec % 60}s` : '0m';

  return (
    <div data-testid="dashboard-page">
      {user?.role === 'manager' && <PaymentFailureBanner />}
      {user?.role === 'manager' && <WelcomeModal userId={user?._id || user?.id} />}
      {(user?.role === 'manager' || user?.role === 'client') && <OnboardingChecklist />}

      {/* First-call celebration — fires once when total_calls === 1 */}
      {showFirstCallCelebration && (
        <div className="mb-6 bg-gradient-to-r from-accent-green/15 to-accent-blue/10 border border-accent-green/30 rounded-lg p-5 flex items-start justify-between gap-4" data-testid="first-call-celebration">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-full bg-accent-green/20 flex items-center justify-center flex-shrink-0">
              <PartyPopper size={22} className="text-accent-green" />
            </div>
            <div>
              <p className="text-white font-semibold text-base">🎉 Din første samtale!</p>
              <p className="text-slate-300 text-sm mt-1">Gratulerer — AI-resepsjonisten din har akkurat tatt sin første samtale. Sjekk samtaleloggen og se hva som ble sagt.</p>
              <button
                onClick={() => {
                  // Mark dismissed BEFORE navigating, otherwise the celebration
                  // re-appears when the user comes back to the dashboard.
                  localStorage.setItem('first-call-celebrated', '1');
                  setShowFirstCallCelebration(false);
                  if (recentCalls[0]) navigate(`/calls/${recentCalls[0].call_id}`);
                }}
                className="mt-3 text-sm px-4 py-2 bg-accent-green hover:bg-accent-green/80 text-white font-medium rounded-md transition-colors"
              >
                Se første samtale →
              </button>
            </div>
          </div>
          <button
            onClick={() => { setShowFirstCallCelebration(false); localStorage.setItem('first-call-celebrated', '1'); }}
            className="text-slate-500 hover:text-white transition-colors flex-shrink-0"
            aria-label="Lukk"
          >
            ✕
          </button>
        </div>
      )}

      {showWelcomeBanner && (
        <div className="mb-6 bg-accent-blue/10 border border-accent-blue/30 rounded-lg p-4 flex items-start justify-between gap-4" data-testid="welcome-banner">
          <div className="flex items-start gap-3">
            <span className="text-2xl">🎉</span>
            <div>
              <p className="text-white font-semibold text-sm">Velkommen til AI Resepsjonen!</p>
              <p className="text-slate-400 text-sm mt-0.5">Agenten din er klar til å ta imot samtaler. Konfigurer innstillinger og se samtaler her.</p>
            </div>
          </div>
          <button
            onClick={() => { setShowWelcomeBanner(false); localStorage.setItem('welcome-banner-dismissed', '1'); }}
            className="text-slate-500 hover:text-white transition-colors flex-shrink-0 mt-0.5"
            aria-label="Lukk velkomstmelding"
          >
            ✕
          </button>
        </div>
      )}
      <div className="mb-8">
        <h1 className="font-heading text-2xl font-bold text-white tracking-tight">
          Hei, {user?.name || 'Velkommen'}
        </h1>
        <p className="text-slate-500 text-sm mt-1">Her er en oversikt over dine samtaler siste 30 dager.</p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          label={t('dash_total_calls')}
          value={a.total_calls || 0}
          icon={Phone}
          iconColor="bg-accent-blue/20 text-accent-blue"
          trend={a.change_pct}
          trendUp={a.change_pct > 0}
          prevCount={a.prev_period_calls}
          subtext={a.prev_period_calls !== undefined ? `vs. ${a.prev_period_calls} forrige 30 dager` : undefined}
        />
        <StatCard
          label={t('dash_hot_leads')}
          value={a.hot_leads || 0}
          icon={Flame}
          iconColor="bg-accent-green/20 text-accent-green"
          subtext="Lead score > 80"
        />
        <StatCard
          label={t('dash_appointments')}
          value={a.appointments_booked || 0}
          icon={Calendar}
          iconColor="bg-purple-500/20 text-purple-400"
        />
        <StatCard
          label={t('dash_avg_duration')}
          value={avgMin}
          icon={Clock}
          iconColor="bg-accent-yellow/20 text-accent-yellow"
        />
        {(a.textbacks_sent || 0) > 0 && (
          <StatCard
            label={t('dash_textbacks')}
            value={a.textbacks_sent || 0}
            icon={MessageCircle}
            iconColor="bg-teal-500/20 text-teal-400"
            subtext="Denne perioden"
          />
        )}
        {pendingSuggestions > 0 && (
          <StatCard
            label={t('suggestions_pending')}
            value={pendingSuggestions}
            icon={Sparkles}
            iconColor="bg-accent-yellow/20 text-accent-yellow"
          />
        )}
      </div>

      {/* Compact usage meter — minutes used vs plan limit */}
      <div className="mb-6">
        <UsageMeter compact={true} />
      </div>

      {/* Test din AI-resepsjonist widget — visible while < 10 calls (onboarding) */}
      {company?.phone_number && (a.total_calls || 0) < 10 && (
        <div className="mb-6 bg-navy-800 border border-accent-blue/30 rounded-lg p-5" data-testid="test-agent-widget">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-accent-blue/20 flex items-center justify-center flex-shrink-0">
                <PhoneCall size={22} className="text-accent-blue" />
              </div>
              <div>
                <p className="text-white font-semibold text-sm">Test din AI-resepsjonist</p>
                <p className="text-slate-400 text-sm mt-0.5">Ring nummeret nå for å høre hvordan agenten din svarer.</p>
                <p className="text-accent-blue font-mono text-base mt-1">{company.phone_number}</p>
              </div>
            </div>
            <a
              href={`tel:${company.phone_number}`}
              className="flex items-center gap-2 px-4 py-2.5 bg-accent-blue hover:bg-accent-blue-hover text-white font-medium rounded-lg transition-colors text-sm"
              data-testid="test-agent-call-link"
            >
              <PhoneCall size={14} /> Ring nå
            </a>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        <QuickAction
          icon={Phone}
          label={t('dash_quick_latest_call', 'Nyeste samtale')}
          subtext={t('dash_quick_latest_call_sub', 'Se siste anrop')}
          color="text-accent-blue"
          disabled={recentCalls.length === 0}
          onClick={() => recentCalls[0] && navigate(`/calls/${recentCalls[0].call_id}`)}
        />
        <QuickAction
          icon={Download}
          label={t('dash_quick_export_week', 'Eksporter uke')}
          subtext={t('dash_quick_export_week_sub', 'Last ned CSV')}
          color="text-accent-green"
          onClick={async () => {
            try {
              const today = new Date();
              const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
              const params = new URLSearchParams({
                format: 'csv',
                start_date: weekAgo.toISOString().slice(0, 10),
                end_date: today.toISOString().slice(0, 10) + 'T23:59:59',
              });
              const response = await api.get(`/api/calls/export?${params}`, { responseType: 'blob' });
              const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute('download', `samtaler-uke-${today.toISOString().slice(0, 10)}.csv`);
              document.body.appendChild(link); link.click(); link.remove();
              window.URL.revokeObjectURL(url);
              toast.success('Ukens samtaler eksportert');
            } catch (err) {
              toast.error('Kunne ikke eksportere — prøv fra Samtaler-siden');
              navigate('/calls');
            }
          }}
        />
        <QuickAction
          icon={Users}
          label={t('dash_quick_contacts', 'Alle kontakter')}
          subtext={t('dash_quick_contacts_sub', 'Bla i kontaktlista')}
          color="text-purple-400"
          onClick={() => navigate('/contacts')}
        />
        <QuickAction
          icon={FileText}
          label={t('dash_quick_analytics', 'Analyse')}
          subtext={t('dash_quick_analytics_sub', 'Trender og innsikt')}
          color="text-accent-yellow"
          onClick={() => navigate('/analytics')}
        />
      </div>

      {/* Go-live status — error fallback */}
      {onboardingError && (
        <div className="mb-6 bg-navy-800 border border-navy-600 rounded-lg p-4 flex items-center justify-between gap-3" data-testid="go-live-card-error">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-slate-500/15 flex items-center justify-center">
              <Rocket size={14} className="text-slate-400" />
            </div>
            <div>
              <p className="text-sm text-slate-300">{t('go_live_error', 'Kunne ikke laste status')}</p>
              <p className="text-xs text-slate-500 mt-0.5">{t('go_live_error_sub', 'Prøv igjen om et øyeblikk.')}</p>
            </div>
          </div>
          <button
            onClick={() => { setOnboardingError(false); setOnboardingRetryNonce(n => n + 1); }}
            className="text-xs px-3 py-1.5 bg-navy-700 border border-navy-600 text-slate-300 rounded-md hover:border-accent-blue hover:text-white transition-colors"
            data-testid="go-live-retry"
          >
            {t('go_live_retry', 'Prøv igjen')}
          </button>
        </div>
      )}

      {/* Go-live status — only render if not all 4 steps complete */}
      {onboardingStatus?.steps && onboardingStatus.completed_count < onboardingStatus.total && (() => {
        const items = [
          { key: 'phone_added', label: 'Telefonnummer aktivt', done: !!onboardingStatus.steps.phone_added },
          { key: 'agent_tested', label: 'Viderekobling testet', done: !!onboardingStatus.steps.agent_tested },
          { key: 'hours_set', label: 'Åpningstider satt', done: !!onboardingStatus.steps.hours_set },
          { key: 'agent_configured', label: 'AI-agent konfigurert', done: !!onboardingStatus.steps.portal_created },
        ];
        const goLiveDone = items.filter(i => i.done).length;
        if (goLiveDone === items.length) return null;
        return (
          <div className="mb-6 bg-navy-800 border border-accent-blue/30 rounded-lg p-5" data-testid="go-live-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-accent-blue/20 flex items-center justify-center">
                  <Rocket size={18} className="text-accent-blue" />
                </div>
                <div>
                  <h3 className="font-heading text-base font-semibold text-white">Go-live status</h3>
                  <p className="text-xs text-slate-400 mt-0.5">{goLiveDone} av {items.length} steg klare for full lansering</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-heading font-bold text-white">{Math.round((goLiveDone / items.length) * 100)}%</div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {items.map(item => (
                <div
                  key={item.key}
                  data-testid={`go-live-${item.key}`}
                  className={`flex items-center gap-3 p-3 rounded-lg border ${item.done ? 'border-accent-green/30 bg-accent-green/5' : 'border-navy-600 bg-navy-900/40'}`}
                >
                  {item.done
                    ? <CheckCircle2 size={18} className="text-accent-green flex-shrink-0" />
                    : <Circle size={18} className="text-slate-500 flex-shrink-0" />}
                  <span className={`text-sm ${item.done ? 'text-slate-200' : 'text-slate-400'}`}>{item.label}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => navigate('/settings')}
                className="text-xs text-accent-blue hover:text-accent-blue-hover transition-colors"
              >
                Fullfør oppsett →
              </button>
            </div>
          </div>
        );
      })()}

      {/* Chart + Recent calls */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chart */}
        <div className="lg:col-span-2 bg-navy-800 border border-navy-600 rounded-lg p-6 flex flex-col">
          <h3 className="font-heading text-base font-semibold text-white mb-4">{t('dash_calls_per_day', 'Samtaler per dag')}</h3>
          <div className="flex-1 min-h-[220px]">
            {a.calls_by_day && a.calls_by_day.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={a.calls_by_day}>
                  <XAxis
                    dataKey="date"
                    tick={{ fill: '#64748B', fontSize: 11 }}
                    tickFormatter={(d) => d.split('-').slice(1).join('/')}
                    axisLine={{ stroke: '#222F43' }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: '#64748B', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#121927', border: '1px solid #222F43', borderRadius: '8px', color: '#F8FAFC' }}
                    labelFormatter={(d) => `Dato: ${d}`}
                    formatter={(val) => [`${val} samtaler`, '']}
                  />
                  <Bar dataKey="count" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center px-6" data-testid="dash-empty-chart">
                <div className="w-12 h-12 rounded-full bg-navy-700 flex items-center justify-center mb-3">
                  <Calendar size={20} className="text-slate-500" />
                </div>
                <p className="text-sm font-medium text-slate-300 mb-1">{t('analytics_no_data')}</p>
                <p className="text-xs text-slate-500 max-w-xs">Grafen viser samtalevolum når innringere begynner å ringe AI-resepsjonisten din.</p>
              </div>
            )}
          </div>
        </div>

        {/* Recent calls */}
        <div className="bg-navy-800 border border-navy-600 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-base font-semibold text-white">{t('dash_recent_calls')}</h3>
            {recentCalls.length > 0 && (
              <button onClick={() => navigate('/calls')} className="text-xs text-accent-blue hover:text-blue-400 transition-colors">Se alle →</button>
            )}
          </div>
          <div className="space-y-3">
            {recentCalls.map((call) => {
              const _ca = call.call_analysis || {};
              const _custom = _ca.custom_analysis_data || {};
              const analysis = { ..._ca, ..._custom,
                caller_name: call.caller_name ?? _ca.caller_name ?? _custom.caller_name,
                lead_score: call.lead_score ?? _ca.lead_score ?? _custom.lead_score,
                call_summary: call.call_summary ?? _ca.call_summary,
              };
              const time = new Date(call.created_at).toLocaleString('nb-NO', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
              return (
                <button
                  key={call._id}
                  onClick={() => navigate(`/calls/${call.call_id}`)}
                  className="w-full text-left p-3 rounded-lg hover:bg-navy-700 transition-colors group"
                  data-testid={`recent-call-${call.call_id}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-white group-hover:text-accent-blue transition-colors">
                      {analysis.caller_name || t('unknown')}
                    </span>
                    <span className={`text-xs font-mono px-2 py-0.5 rounded ${
                      (analysis.lead_score || 0) >= 80 ? 'bg-accent-green/20 text-accent-green' :
                      (analysis.lead_score || 0) >= 50 ? 'bg-accent-yellow/20 text-accent-yellow' :
                      'bg-slate-500/20 text-slate-400'
                    }`}>
                      {analysis.lead_score || 0}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{analysis.call_summary?.substring(0, 60)}...</div>
                  <div className="text-xs text-slate-500 mt-1">{time}</div>
                </button>
              );
            })}
            {recentCalls.length === 0 && (
              <div className="flex flex-col items-center justify-center text-center py-8" data-testid="dash-empty-recent">
                <div className="w-14 h-14 rounded-full bg-accent-blue/10 flex items-center justify-center mb-4">
                  <Phone size={22} className="text-accent-blue" />
                </div>
                <p className="text-sm font-medium text-white mb-1">{t('dash_no_calls')}</p>
                <p className="text-xs text-slate-500 max-w-[220px] leading-relaxed">
                  Samtaler dukker opp her så snart AI-resepsjonisten din mottar sin første samtale.
                </p>
                <button
                  onClick={() => navigate('/settings')}
                  className="mt-4 text-xs px-3 py-1.5 bg-accent-blue hover:bg-blue-600 text-white rounded-md transition-colors"
                >
                  Sjekk innstillinger
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Today's calls — Dagens samtaler */}
      {(() => {
        const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
        const todayCalls = (recentCalls || []).filter(c => new Date(c.created_at) >= todayStart).slice(0, 5);
        // Always show the section — empty state is informative
        const outcomeBadge = (c) => {
          const ca = c.call_analysis || {};
          const custom = ca.custom_analysis_data || {};
          if (c.appointment_booked || ca.appointment_booked || custom.appointment_booked) return { label: 'Avtale', cls: 'bg-purple-500/20 text-purple-300' };
          if (c.callback_requested || ca.callback_requested || custom.callback_requested) return { label: 'Tilbakeringning', cls: 'bg-accent-blue/20 text-accent-blue' };
          return { label: 'Annet', cls: 'bg-slate-500/20 text-slate-400' };
        };
        return (
          <div className="mt-6 bg-navy-800 border border-navy-600 rounded-lg p-6" data-testid="todays-calls">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-accent-yellow/15 flex items-center justify-center">
                  <Sun size={15} className="text-accent-yellow" />
                </div>
                <h3 className="font-heading text-base font-semibold text-white">Dagens samtaler</h3>
                <span className="text-xs px-2 py-0.5 rounded-full bg-navy-700 text-slate-400">{todayCalls.length}</span>
              </div>
              <button onClick={() => navigate('/calls?range=1d')} className="text-xs text-accent-blue hover:text-accent-blue-hover transition-colors">Se alle →</button>
            </div>
            {todayCalls.length === 0 ? (
              <div className="py-6 text-center">
                <p className="text-sm text-slate-500">Ingen samtaler i dag ennå</p>
                <p className="text-xs text-slate-600 mt-1">Samtaler vil vises her løpende når de kommer inn</p>
              </div>
            ) : (
            <div className="divide-y divide-navy-700">
              {todayCalls.map(call => {
                const ca = call.call_analysis || {};
                const custom = ca.custom_analysis_data || {};
                const callerName = call.caller_name || ca.caller_name || custom.caller_name || 'Ukjent';
                const time = new Date(call.created_at).toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });
                const o = outcomeBadge(call);
                return (
                  <button
                    key={call._id}
                    onClick={() => navigate(`/calls/${call.call_id}`)}
                    className="w-full flex items-center justify-between py-3 hover:bg-navy-700/40 transition-colors group px-2 -mx-2 rounded"
                    data-testid={`today-call-${call.call_id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-full bg-navy-700 flex items-center justify-center flex-shrink-0">
                        <Phone size={13} className="text-slate-400" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-white group-hover:text-accent-blue transition-colors truncate">{callerName}</div>
                        <div className="text-xs text-slate-500">{time}</div>
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${o.cls}`}>{o.label}</span>
                  </button>
                );
              })}
            </div>
            )}
          </div>
        );
      })()}

      {/* Hot Leads Leaderboard */}
      <div className="mt-6">
        <HotLeadsLeaderboard />
      </div>
    </div>
  );
}
